import { useSiteSettings } from "@/hooks/useSiteSettings";
import { ContactInfo } from "@/components/ContactInfo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        path="/privacy-policy"
        title="Privacy Policy — In Paradise Beauty"
        description="Read how In Paradise Beauty collects, uses, and protects your personal information and appointment data."
      />
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-3">Legal</p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold italic">Privacy Policy</h1>
          </div>

          <div className="prose prose-sm max-w-none text-muted-foreground font-light leading-[2] tracking-wide space-y-8">
            <p className="text-xs text-muted-foreground/70 uppercase tracking-[0.3em]">
              Last Updated: April 19, 2026
            </p>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">1. Introduction</h2>
              <p>
                In Paradise Beauty ("we" or "us") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit the website, use the mobile application, book appointments, or otherwise use the services.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">2. Information We Collect</h2>
              <p>We may collect the following types of personal information:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong className="text-foreground">Contact Information:</strong> Name, email address, phone number</li>
                <li><strong className="text-foreground">Booking Information:</strong> Appointment dates, times, services selected, and booking preferences</li>
                <li><strong className="text-foreground">Account Information:</strong> Login credentials and profile details</li>
                <li><strong className="text-foreground">Payment Information:</strong> Deposit and payment records (processed securely through third-party providers)</li>
                <li><strong className="text-foreground">Communication Records:</strong> Emails, SMS messages, and other correspondence</li>
                <li><strong className="text-foreground">Usage Data:</strong> Browser type, IP address, pages visited, and other analytics data</li>
                <li><strong className="text-foreground">Mobile Device Data:</strong> When using the mobile app, we may collect device identifiers, operating system version, app version, crash diagnostics, and approximate location used to display the salon address. We do not collect precise GPS location.</li>
                <li><strong className="text-foreground">Photos &amp; Camera:</strong> If you upload reference or post-service photos, the images and any photo metadata you submit are stored on the backend. The app only accesses your camera or photo library after you grant permission, and only for the action you initiated.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">3. How We Use Your Information</h2>
              <p>We use your personal information for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>To process and manage your appointments</li>
                <li>To send booking confirmations, reminders, and updates via email and/or SMS</li>
                <li>To communicate with you about the services, promotions, and updates</li>
                <li>To process payments and deposits</li>
                <li>To improve the website and services</li>
                <li>To comply with legal obligations</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">4. SMS & Email Communications</h2>
              <p>
                With your consent, we may send you SMS text messages and emails related to your appointments and the services. These communications are powered by third-party services (Twilio for SMS, Resend for email). Message and data rates may apply. You can opt out at any time by replying STOP to any SMS or using the unsubscribe link in the emails.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">5. Data Sharing & Disclosure</h2>
              <p>
                We do not sell, trade, or rent your personal information to third parties. We may share your data with:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Service providers who assist us in operating the business (e.g., email/SMS providers, payment processors)</li>
                <li>Legal authorities when required by law or to protect legal rights</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">6. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">7. Data Retention</h2>
              <p>
                We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, or as required by law.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">8. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt out of marketing communications</li>
                <li>Withdraw your consent at any time</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">9. Account Deletion</h2>
              <p>
                You may delete your account and associated personal data at any time directly from within the app. Sign in, go to <strong className="text-foreground">Account → Delete Account</strong>, and follow the on-screen confirmation. Deletion is permanent and removes your profile, bookings, photos, rewards, and sign-in credentials. Limited records that we are legally required to retain (such as tax or payment receipts) may be kept in anonymized form. You can also email us at the address below to request deletion.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">10. Children's Privacy</h2>
              <p>
                Services are not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided us personal information, please contact us so we can remove it.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">11. Push Notifications &amp; Permissions</h2>
              <p>
                The mobile app may request permission to send push notifications (for booking confirmations, reminders, and updates) and to access your camera or photo library (only when you choose to upload a photo). You can change or revoke any of these permissions at any time in your device settings.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">12. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <ContactInfo />
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
